import { useEffect, useState, useRef } from 'react';
import { useWebSocket } from './hooks/useWebSocket';
import ReactMarkdown from 'react-markdown';
import { Header } from './components/Header';
import AudioWave from './components/AudioWave';
import WebcamVideo from './components/WebcamVideo';
import './App.css';

// Estados de la entrevista
enum InterviewState {
  INITIAL = 'INITIAL',
  CONNECTING = 'CONNECTING',
  READY = 'READY',
  GENERATING_ANALYSIS = 'GENERATING_ANALYSIS',
  FINISHED = 'FINISHED'
}

function App() {
  // Estados
  const [interviewState, setInterviewState] = useState<InterviewState>(InterviewState.INITIAL);
  const [jobInfo, setJobInfo] = useState('');
  const [messages, setMessages] = useState<Array<{ type: 'user' | 'interviewer', text: string }>>([]);
  const [analysis, setAnalysis] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isRecording, setIsRecording] = useState(false);
  const [canRecord, setCanRecord] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const audioQueueRef = useRef<Uint8Array[]>([]);
  const isPlayingRef = useRef(false);

  // Scroll al final cuando hay nuevos mensajes
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Inicializar AudioContext
  useEffect(() => {
    audioContextRef.current = new AudioContext();
    return () => {
      audioContextRef.current?.close();
    };
  }, []);

  // Función para reproducir audio
  const playAudioChunk = async (base64Audio: string) => {
    if (!audioContextRef.current) return;

    try {
      // Decodificar base64 a Uint8Array
      const binaryString = atob(base64Audio);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Agregar a la cola
      audioQueueRef.current.push(bytes);

      // Iniciar reproducción si no está activa
      if (!isPlayingRef.current) {
        isPlayingRef.current = true;
        processAudioQueue();
      }
    } catch (error) {
      console.error('Error procesando chunk de audio');
    }
  };

  // Procesar cola de audio
  const processAudioQueue = async () => {
    const BUFFER_SIZE = 3; // Número de chunks a pre-bufferar
    const SAMPLE_RATE = 16000; // Sample rate del audio PCM
    let currentTime = audioContextRef.current?.currentTime || 0;
    let bufferUnderrun = false;
    
    while (audioQueueRef.current.length > 0 && audioContextRef.current) {
      try {
        // Esperar a tener suficientes chunks o ser el último grupo
        if (audioQueueRef.current.length < BUFFER_SIZE && isPlayingRef.current && !bufferUnderrun) {
          bufferUnderrun = true;
          await new Promise(resolve => setTimeout(resolve, 50));
          continue;
        }

        bufferUnderrun = false;
        const chunksToProcess = Math.min(audioQueueRef.current.length, BUFFER_SIZE);
        const scheduledEndTime = currentTime;

        for (let i = 0; i < chunksToProcess; i++) {
          const chunk = audioQueueRef.current.shift();
          if (!chunk) continue;

          // Crear buffer de audio con el tamaño exacto para los samples PCM
          const numSamples = Math.floor(chunk.length / 2); // 2 bytes por sample (16-bit)
          const audioBuffer = audioContextRef.current.createBuffer(1, numSamples, SAMPLE_RATE);
          const channelData = audioBuffer.getChannelData(0);
          
          // Asegurar que tenemos un ArrayBuffer válido
          const arrayBuffer = chunk.buffer.slice(chunk.byteOffset, chunk.byteOffset + chunk.length);
          const view = new DataView(arrayBuffer);
          
          // Convertir PCM 16-bit little-endian a float32
          for (let j = 0; j < numSamples; j++) {
            const pcm16 = view.getInt16(j * 2, true); // true para little-endian
            channelData[j] = pcm16 / 32768.0; // Normalizar a [-1, 1]
          }
          
          const source = audioContextRef.current.createBufferSource();
          source.buffer = audioBuffer;
          source.connect(audioContextRef.current.destination);
          
          // Programar la reproducción
          const startTime = currentTime;
          source.start(startTime);
          currentTime += audioBuffer.duration;
        }

        // Si hay más chunks en la cola, esperar un poco antes de procesar el siguiente grupo
        if (audioQueueRef.current.length > 0) {
          const timeToNextChunk = Math.max(0, scheduledEndTime - audioContextRef.current.currentTime);
          await new Promise(resolve => setTimeout(resolve, timeToNextChunk * 1000));
        }
      } catch (error) {
        console.error('Error procesando chunk de audio');
        // Continuar con el siguiente chunk en caso de error
        continue;
      }
    }
    
    isPlayingRef.current = false;
  };

  // WebSocket hook
  const { sendMessage, sendBinaryMessage, wsRef, initWebSocket } = useWebSocket({
    onClose: () => {
      // Si la entrevista está en curso y se cierra el WebSocket, mostrar error
      if (interviewState === InterviewState.READY) {
        setError('Se perdió la conexión con el entrevistador');
        setInterviewState(InterviewState.INITIAL);
      }
    },
    onMessage: async (data) => {
      try {
        switch (data.type) {
          case 'status':
            if (data.message === "Entrevistador listo") {
              setInterviewState(InterviewState.READY);
              setMessages(prev => [...prev, {
                type: 'interviewer',
                text: "¡Hola! Por favor, preséntate y cuéntame un poco sobre ti."
              }]);
              setCanRecord(true);
            }
            break;
          case 'text_chunk':
            setMessages(prev => {
              const newMessages = [...prev];
              const lastMessage = newMessages[newMessages.length - 1];
              
              if (lastMessage && lastMessage.type === 'interviewer') {
                lastMessage.text += data.text;
                return newMessages;
              } else {
                return [...prev, { type: 'interviewer', text: data.text }];
              }
            });
            scrollToBottom();
            break;
          case 'analysis':
            if (data.text === "Generando análisis de la entrevista...") {
              setInterviewState(InterviewState.GENERATING_ANALYSIS);
            } else {
              setAnalysis(data.text);
              setInterviewState(InterviewState.FINISHED);
            }
            break;
          case 'transcription':
            setMessages(prev => {
              const newMessages = [...prev];
              const lastMessage = newMessages[newMessages.length - 1];
              if (lastMessage && lastMessage.type === 'user' && lastMessage.text === '🎤 Audio enviado...') {
                lastMessage.text = data.text;
              }
              return newMessages;
            });
            scrollToBottom();
            break;
          case 'response_complete':
            setCanRecord(true);
            scrollToBottom();
            break;
          case 'audio_chunk':
            await playAudioChunk(data.audio);
            break;
          case 'error':
            setError(data.message);
            setCanRecord(true); // Re-enable recording on error
            break;
        }
      } catch (error) {
        console.error('Error processing message:', error);
        setError('Error procesando mensaje del servidor');
      }
    },
    onError: (error) => {
      console.error('WebSocket error:', error);
      setError('Error de conexión');
    }
  });

  // Funciones de grabación
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm; codecs=opus'
      });
      const chunks: BlobPart[] = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        try {
          const opusBlob = new Blob(chunks, { type: 'audio/webm; codecs=opus' });
          if (opusBlob.size === 0) {
            console.error('[Audio] Error: blob vacío');
            return;
          }

          setCanRecord(false);
          
          // Agregar mensaje del usuario
          setMessages(prev => [...prev, {
            type: 'user',
            text: '🎤 Audio enviado...'
          }]);

          // Enviar audio Opus directamente
          await sendBinaryMessage(opusBlob);

          // Limpiar chunks
          chunks.length = 0;
        } catch (error) {
          console.error('Error procesando audio grabado:', error);
          setError('Error al procesar el audio');
          setCanRecord(true);
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error al iniciar la grabación:', error);
      setError('Error al acceder al micrófono');
      setCanRecord(true);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // Efectos
  useEffect(() => {
    if (interviewState === InterviewState.READY) {
      setCanRecord(true);
    } else {
      setCanRecord(false);
      if (mediaRecorderRef.current?.state === 'recording') {
        stopRecording();
      }
    }
  }, [interviewState]);

  // Handlers
  const handleStartInterview = async () => {
    if (!jobInfo.trim()) {
      setError('Por favor, introduce la información del puesto');
      return;
    }

    setInterviewState(InterviewState.CONNECTING);
    setError('');

    try {
      // Iniciar nueva conexión WebSocket
      initWebSocket();
      
      // Esperar un breve momento para asegurar que la conexión esté lista
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      if (wsRef.current?.readyState !== WebSocket.OPEN) {
        throw new Error('No se pudo establecer la conexión');
      }
      
      // Enviar información del trabajo
      sendMessage({
        type: 'job_info',
        data: jobInfo
      });
    } catch (error) {
      console.error('Error starting interview:', error);
      setError('Error iniciando la entrevista');
      setInterviewState(InterviewState.INITIAL);
      if (wsRef.current) {
        wsRef.current.close();
      }
    }
  };

  const handleNewInterview = () => {
    // Cerrar la conexión WebSocket si existe
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    setInterviewState(InterviewState.INITIAL);
    setJobInfo('');
    setMessages([]);
    setAnalysis('');
    setError('');
    setIsRecording(false);
    setCanRecord(false);
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.close();
    }
  };

  // Renderizado condicional basado en el estado
  if (interviewState === InterviewState.INITIAL) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col">
        <Header />
        <div className="flex-1 py-6 flex flex-col justify-center">
          <div className="relative py-3 sm:max-w-xl sm:mx-auto">
            <div className="relative px-4 py-10 bg-white shadow-lg sm:rounded-3xl sm:p-20">
              <div className="max-w-md mx-auto">
                <div className="divide-y divide-gray-200">
                  <div className="py-8 text-base leading-6 space-y-4 text-gray-700 sm:text-lg sm:leading-7">
                    <h2 className="text-2xl font-bold mb-4">Simulador de Entrevista</h2>
                    <p className="mb-4">Por favor, introduce la información del puesto para el cual quieres practicar:</p>
                    <textarea
                      value={jobInfo}
                      onChange={(e) => setJobInfo(e.target.value)}
                      className="w-full p-2 border rounded"
                      rows={6}
                      placeholder="Título del puesto, responsabilidades, requisitos técnicos, experiencia requerida..."
                    />
                    {error && <p className="text-red-500">{error}</p>}
                    <button
                      onClick={handleStartInterview}
                      className="mt-4 w-full bg-blue-500 text-white p-2 rounded hover:bg-blue-600"
                    >
                      Comenzar Entrevista
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (interviewState === InterviewState.CONNECTING) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Conectando con el entrevistador...</p>
          </div>
        </div>
      </div>
    );
  }

  if (interviewState === InterviewState.GENERATING_ANALYSIS) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">La entrevista ha finalizado</p>
            <p className="mt-2 text-gray-600">Generando análisis...</p>
          </div>
        </div>
      </div>
    );
  }

  if (interviewState === InterviewState.FINISHED) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col">
        <Header />
        <div className="flex-1 py-6">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl font-bold mb-4">Análisis de la Entrevista</h2>
            <div className="bg-white shadow-lg rounded-lg p-6 prose prose-slate max-w-none">
              <ReactMarkdown>
                {analysis}
              </ReactMarkdown>
              <button
                onClick={handleNewInterview}
                className="mt-8 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
              >
                Nueva Entrevista
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <Header />
      <div className="flex-1 w-full mx-auto p-4">
        <div className="grid grid-cols-2 gap-4 h-[calc(100vh-8rem)] max-h-[calc(100vh-8rem)]">
          {/* Webcam */}
          <div className="bg-white rounded-lg shadow-lg p-4 h-full">
            <WebcamVideo />
          </div>
          
          {/* Chat */}
          <div className="bg-white rounded-lg shadow-lg p-4 flex flex-col h-full overflow-hidden">
            {/* Mensajes */}
            <div className="flex-1 overflow-y-auto mb-4">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`mb-4 ${
                    message.type === 'user' ? 'text-right' : 'text-left'
                  }`}
                >
                  <div
                    className={`inline-block p-4 rounded-lg ${
                      message.type === 'user'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-800'
                    }`}
                  >
                    {message.text}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Control de grabación */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-center">
                {canRecord ? (
                  <div className="text-center">
                    <div className="flex flex-col items-center">
                      <button
                        onClick={isRecording ? stopRecording : startRecording}
                        className={`px-4 py-2 rounded-full ${
                          isRecording 
                            ? 'bg-red-500 hover:bg-red-600' 
                            : 'bg-blue-500 hover:bg-blue-600'
                        } text-white transition-colors`}
                      >
                        {isRecording ? (
                          <div className="flex items-center">
                          <span className="mr-2">⬤</span> Enviar respuesta
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <span className="mr-2">🎤</span> Hablar con Aura
                          </div>
                        )}
                      </button>
                      <AudioWave isRecording={isRecording} />
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-500">
                    Esperando respuesta del entrevistador...
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
